import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { ArrowLeft, Download } from "lucide-react";
import { fetchHistory } from "@/lib/api";
import { fmtInt, fmtSigned, signClass, fmtCompact, fmtPct } from "@/lib/format";
import { toast } from "sonner";

const WEEKS_6M = 27; // ~6 months of weekly reports

function MonoTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-zinc-700 bg-black px-3 py-2 font-mono">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 text-[11px] tabular-nums">
          <span className="inline-block h-2 w-2" style={{ background: p.color }} />
          <span className="text-zinc-400 uppercase tracking-wider">{p.name}</span>
          <span className="ml-auto text-zinc-100">{fmtSigned(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function MarketDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const histQ = useQuery({
    queryKey: ["history-detail", code, WEEKS_6M],
    queryFn: () => fetchHistory(code, WEEKS_6M),
    enabled: !!code,
  });

  useEffect(() => {
    if (histQ.error) toast.error("Failed to load market history");
  }, [histQ.error]);

  const rows = histQ.data?.rows ?? [];
  const latest = rows[rows.length - 1];
  const first = rows[0];

  const chartData = rows.map((r) => ({
    date: r.report_date,
    "Net Non-Comm": r.net_noncomm,
    "Net Commercial": r.net_comm,
    "Net Non-Reportable": r.net_nonrept,
    "Open Interest": r.open_interest,
    "NC Long": r.noncomm_long,
    "NC Short": -r.noncomm_short,
    "C Long": r.comm_long,
    "C Short": -r.comm_short,
    "pct_nc_long": r.pct_noncomm_long,
    "pct_nc_short": r.pct_noncomm_short,
    "pct_c_long": r.pct_comm_long,
    "pct_c_short": r.pct_comm_short,
  }));

  const pieData = latest
    ? [
        { name: "Non-Comm Long", value: latest.noncomm_long, color: "#06B6D4" },
        { name: "Non-Comm Short", value: latest.noncomm_short, color: "#0E7490" },
        { name: "Comm Long", value: latest.comm_long, color: "#F59E0B" },
        { name: "Comm Short", value: latest.comm_short, color: "#B45309" },
        { name: "Non-Rept Long", value: latest.nonrept_long, color: "#A78BFA" },
        { name: "Non-Rept Short", value: latest.nonrept_short, color: "#6D28D9" },
      ]
    : [];

  const exportCSV = () => {
    if (!rows.length) return;
    const headers = [
      "report_date",
      "open_interest",
      "change_open_interest",
      "noncomm_long",
      "noncomm_short",
      "net_noncomm",
      "change_net_noncomm",
      "comm_long",
      "comm_short",
      "net_comm",
      "change_net_comm",
      "nonrept_long",
      "nonrept_short",
      "net_nonrept",
    ];
    const csv = [headers.join(",")]
      .concat(rows.map((r) => headers.map((h) => r[h]).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cot_${histQ.data?.symbol || code}_6m.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="market-detail">
      <header className="border-b border-zinc-800 bg-black/95 backdrop-blur sticky top-0 z-20">
        <div className="px-3 lg:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="back-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-700 px-2 py-1 text-[11px] uppercase tracking-widest text-zinc-300 hover:border-amber-500 hover:text-amber-term"
            >
              <ArrowLeft size={12} strokeWidth={1.5} /> Back
            </Link>
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 bg-amber-500 blink" aria-hidden />
              <span className="text-sm font-semibold tracking-[0.18em] uppercase text-amber-term">
                {histQ.data?.symbol || "—"} · {histQ.data?.name || "…"}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 hidden md:inline">
                {histQ.data?.category}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">
              <span className="text-zinc-100">{latest?.report_date || "—"}</span>
              <span className="text-zinc-700"> ← </span>
              <span>{first?.report_date || "—"}</span>
              <span className="text-zinc-700"> · </span>
              <span>{rows.length} WK</span>
            </div>
            <button
              onClick={exportCSV}
              data-testid="csv-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-700 px-2 py-1 text-[11px] uppercase tracking-widest text-zinc-300 hover:border-amber-500 hover:text-amber-term"
            >
              <Download size={12} strokeWidth={1.5} /> CSV
            </button>
          </div>
        </div>
      </header>

      <main className="px-3 lg:px-6 py-3 space-y-3">
        {histQ.isLoading && (
          <div className="border border-zinc-800 bg-[#09090B] p-6 text-zinc-500 text-xs uppercase tracking-widest text-center">
            Loading 6-month history…
          </div>
        )}

        {!histQ.isLoading && latest && (
          <>
            <KPIStrip latest={latest} rows={rows} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <ChartCard
                title="Net Positioning · 6-Month History"
                testid="chart-net-positioning"
                className="lg:col-span-2"
              >
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => (d ? d.slice(5) : "")} minTickGap={20} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={fmtInt} width={70} />
                  <Tooltip content={<MonoTooltip />} cursor={{ stroke: "#F59E0B", strokeDasharray: "2 2" }} />
                  <Legend wrapperStyle={legendStyle} iconType="square" />
                  <ReferenceLine y={0} stroke="#3F3F46" strokeDasharray="2 2" />
                  <Line type="linear" dataKey="Net Non-Comm" stroke="#06B6D4" strokeWidth={1.5} dot={false} />
                  <Line type="linear" dataKey="Net Commercial" stroke="#F59E0B" strokeWidth={1.5} dot={false} />
                  <Line type="linear" dataKey="Net Non-Reportable" stroke="#A78BFA" strokeWidth={1.5} dot={false} />
                </ComposedChart>
              </ChartCard>

              <ChartCard title="Latest Composition" testid="chart-composition">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} stroke="#000">
                    {pieData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<MonoTooltip />} />
                  <Legend wrapperStyle={legendStyle} iconType="square" />
                </PieChart>
              </ChartCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <ChartCard title="Long vs Short · Non-Commercial" testid="chart-nc-bars">
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={fmtInt} width={70} />
                  <Tooltip content={<MonoTooltip />} />
                  <Legend wrapperStyle={legendStyle} iconType="square" />
                  <ReferenceLine y={0} stroke="#3F3F46" />
                  <Bar dataKey="NC Long" stackId="nc" fill="#22C55E" />
                  <Bar dataKey="NC Short" stackId="nc" fill="#EF4444" />
                </ComposedChart>
              </ChartCard>

              <ChartCard title="Long vs Short · Commercial" testid="chart-c-bars">
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={fmtInt} width={70} />
                  <Tooltip content={<MonoTooltip />} />
                  <Legend wrapperStyle={legendStyle} iconType="square" />
                  <ReferenceLine y={0} stroke="#3F3F46" />
                  <Bar dataKey="C Long" stackId="c" fill="#22C55E" />
                  <Bar dataKey="C Short" stackId="c" fill="#EF4444" />
                </ComposedChart>
              </ChartCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <ChartCard title="Open Interest · 6 Months" testid="chart-oi">
                <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="oiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={fmtCompact} width={60} />
                  <Tooltip content={<MonoTooltip />} />
                  <Area type="linear" dataKey="Open Interest" stroke="#F59E0B" strokeWidth={1.5} fill="url(#oiGrad)" />
                </AreaChart>
              </ChartCard>

              <ChartCard title="% of OI · Long Share" testid="chart-pct">
                <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#27272A" tickFormatter={(v) => `${v}%`} width={45} />
                  <Tooltip content={<MonoTooltip />} />
                  <Legend wrapperStyle={legendStyle} iconType="square" />
                  <Area type="linear" dataKey="pct_nc_long" name="NC Long %" stroke="#06B6D4" strokeWidth={1.5} fill="#06B6D4" fillOpacity={0.15} />
                  <Area type="linear" dataKey="pct_c_long" name="C Long %" stroke="#F59E0B" strokeWidth={1.5} fill="#F59E0B" fillOpacity={0.15} />
                </AreaChart>
              </ChartCard>
            </div>

            <HistoryTable rows={rows} />
          </>
        )}

        <footer className="text-[11px] uppercase tracking-widest text-zinc-600 pt-2 pb-6">
          Source: CFTC Public Reporting · Legacy Futures Only · Released weekly (Fridays)
        </footer>
      </main>
    </div>
  );
}

const legendStyle = {
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "#A1A1AA",
  fontFamily: "JetBrains Mono",
};

function ChartCard({ title, children, className = "", testid }) {
  return (
    <div className={`border border-zinc-800 bg-[#09090B] ${className}`} data-testid={testid}>
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-400">
        ▍ {title}
      </div>
      <div className="p-2 h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function KPIStrip({ latest, rows }) {
  const prev = rows[rows.length - 2];
  const chgOiPct = prev?.open_interest
    ? ((latest.open_interest - prev.open_interest) / prev.open_interest) * 100
    : 0;
  const cards = [
    { label: "Open Interest", value: fmtInt(latest.open_interest), sub: fmtSigned(latest.change_open_interest) + " WoW", subCls: signClass(latest.change_open_interest) },
    { label: "% Δ OI", value: `${chgOiPct >= 0 ? "+" : ""}${chgOiPct.toFixed(2)}%`, sub: "vs prev week", subCls: signClass(chgOiPct) },
    { label: "Net Non-Comm", value: fmtSigned(latest.net_noncomm), sub: fmtSigned(latest.change_net_noncomm) + " WoW", subCls: signClass(latest.change_net_noncomm) },
    { label: "Net Commercial", value: fmtSigned(latest.net_comm), sub: fmtSigned(latest.change_net_comm) + " WoW", subCls: signClass(latest.change_net_comm) },
    { label: "NC Long %", value: fmtPct(latest.pct_noncomm_long), sub: `NC Short ${fmtPct(latest.pct_noncomm_short)}`, subCls: "text-zinc-500" },
    { label: "C Long %", value: fmtPct(latest.pct_comm_long), sub: `C Short ${fmtPct(latest.pct_comm_short)}`, subCls: "text-zinc-500" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
      {cards.map((c, i) => (
        <div key={i} className="border border-zinc-800 bg-[#09090B] p-3 hover:border-zinc-600 transition-colors duration-75" data-testid={`kpi-${i}`}>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">{c.label}</div>
          <div className="text-lg font-semibold text-zinc-100 tabular-nums leading-none">{c.value}</div>
          <div className={`text-[11px] mt-1.5 tabular-nums ${c.subCls}`}>{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

function HistoryTable({ rows }) {
  const sorted = [...rows].sort((a, b) => (a.report_date < b.report_date ? 1 : -1));
  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="history-table-wrapper">
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-400">
        ▍ Weekly History · {rows.length} reports
      </div>
      <div className="overflow-x-auto max-h-[480px]">
        <table className="w-full text-xs whitespace-nowrap" data-testid="history-table">
          <thead className="sticky top-0 bg-zinc-950">
            <tr className="border-b border-zinc-700 text-zinc-400 uppercase tracking-wider">
              {[
                "Date",
                "Open Int",
                "Δ OI",
                "NC Long",
                "NC Short",
                "Net NC",
                "Δ Net NC",
                "C Long",
                "C Short",
                "Net C",
                "Δ Net C",
                "NRep Long",
                "NRep Short",
              ].map((h) => (
                <th key={h} className={`py-2 px-2 font-normal ${h === "Date" ? "text-left" : "text-right"}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.report_date} className="border-b border-zinc-900 hover:bg-zinc-900">
                <td className="py-1.5 px-2 text-zinc-100">{r.report_date}</td>
                <td className="py-1.5 px-2 text-right tabular-nums">{fmtInt(r.open_interest)}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.change_open_interest)}`}>{fmtSigned(r.change_open_interest)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-pos">{fmtInt(r.noncomm_long)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-neg">{fmtInt(r.noncomm_short)}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${signClass(r.net_noncomm)}`}>{fmtSigned(r.net_noncomm)}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.change_net_noncomm)}`}>{fmtSigned(r.change_net_noncomm)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-pos">{fmtInt(r.comm_long)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-neg">{fmtInt(r.comm_short)}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.net_comm)}`}>{fmtSigned(r.net_comm)}</td>
                <td className={`py-1.5 px-2 text-right tabular-nums ${signClass(r.change_net_comm)}`}>{fmtSigned(r.change_net_comm)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{fmtInt(r.nonrept_long)}</td>
                <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{fmtInt(r.nonrept_short)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
